import { useAuth } from 'hooks/useAuth'
import { useStore } from 'hooks/useStore'

/**
 * True while the blocking profile-completion modal must be shown.
 *
 * Support agents impersonating the account cannot know its temporary password, so the modal would trap
 * them; deleted accounts are on their way to recovery instead. The modal gate and the terms modal both
 * read this hook, so they cannot disagree about whether the blocking modal is up.
 */
export function useProfileCompletionRequired(): boolean {
	const { token, impersonateMode } = useAuth()
	const { user } = useStore()

	return !!token && !!user?.profileCompletionRequired && !user.isDeleted && !impersonateMode
}

/**
 * True while a page must stay hidden behind the profile-completion modal: the modal is up, or the profile
 * request for this session has not finished, so whether it is needed is not known yet. A failed request
 * does not block, so the page is never held back forever.
 */
export function useProfileCompletionBlocking(): boolean {
	const { token } = useAuth()
	const { profileStatus } = useStore()
	const required = useProfileCompletionRequired()

	// A failed request is excluded outright: the persisted user it leaves behind is unconfirmed, and the gate
	// will not open the modal for it, so blocking on it would hold the page back with nothing to show.
	return !!token && profileStatus !== 'failed' && (required || profileStatus === 'pending')
}
